import { useState } from "react";
import { CalendarClock, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SLOTS } from "@/components/finance/SavingActions";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

export const LeadFacts = ({ lead }) => {
  const { money, date } = useFormat();
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="rounded-full">{lead.sector_label}</Badge>
        <Badge variant="outline" className="rounded-full">{lead.client_kind}</Badge>
        {lead.mode && <Badge variant="secondary" className="rounded-full">{lead.mode === "exclusive" ? "Exclusif" : `Partagé · ${lead.buyers}/3`}</Badge>}
      </div>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
        <div><div className="text-base font-bold">{lead.charge_type}</div><div className="text-xs text-muted-foreground">Fournisseur actuel : {lead.charge_name}</div></div>
        <div className="text-right"><div className="num text-xl font-extrabold">{money(lead.monthly_cost, { maximumFractionDigits: 2 })}<span className="text-xs font-normal">/mois</span></div>
          <div className="num text-xs text-muted-foreground">{money(lead.monthly_cost * 12, { maximumFractionDigits: 0 })}/an</div></div>
      </div>
      {lead.details?.length > 0 && (
        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-border p-2.5 text-xs" data-testid={`lead-details-${lead.id}`}>{lead.details.map((d) => (
          <div key={d.label} className="min-w-0"><dt className="text-muted-foreground">{d.label}</dt><dd className="font-semibold">{d.value}</dd></div>))}</dl>)}
      <div className="mt-3 flex items-start gap-2 rounded-lg bg-muted/60 p-2.5 text-xs">
        <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Disponible du <b>{date(lead.from_date)}</b> au <b>{date(lead.to_date)}</b> · {lead.slots.map((s) => SLOTS[s]).join(", ")}</span>
      </div>
      <div className="mt-3 text-xs">
        <div className="font-semibold">Dépenses {lead.sector_label.toLowerCase()} du client · {money(lead.sector_total, { maximumFractionDigits: 2 })}/mois</div>
        <ul className="mt-1 space-y-0.5 text-muted-foreground">{lead.sector_expenses.map((e) => <li key={e.name} className="flex justify-between"><span>{e.name}</span><span className="num">{money(e.monthly_cost, { maximumFractionDigits: 2 })}/mois</span></li>)}</ul>
      </div>
      {lead.note && <p className="mt-2 text-xs italic text-muted-foreground">« {lead.note} »</p>}
    </>
  );
};

export const LeadCard = ({ lead, prices }) => {
  const [busy, setBusy] = useState("");
  const buy = async (mode) => {
    setBusy(mode);
    try {
      const { data } = await api.post(`/partners/leads/${lead.id}/checkout`, { mode, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  return (
    <li className="card-surface flex flex-col p-5" data-testid={`lead-${lead.id}`}>
      <LeadFacts lead={lead} />
      <div className="mt-4 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Coordonnées visibles après achat</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Button variant="outline" className="rounded-full" disabled={!lead.shared_left || !!busy} onClick={() => buy("shared")} data-testid={`lead-buy-shared-${lead.id}`}>
          <Users className="mr-1 h-4 w-4" /> Partagé {prices.shared} € HT · {lead.shared_left} place{lead.shared_left > 1 ? "s" : ""}</Button>
        <Button className="rounded-full" disabled={!lead.exclusive_available || !!busy} onClick={() => buy("exclusive")} data-testid={`lead-buy-exclusive-${lead.id}`}>
          Exclusif {prices.exclusive} € HT</Button>
      </div>
    </li>
  );
};
